/**
 * The one sentence at the top of the overview: why nothing is going out, or the
 * fact that nothing is in the way.
 *
 * IT ALWAYS SAYS SOMETHING. Silence on this question is how a module that is
 * idle by design reads as a module that is broken - and this one is idle by
 * design until a sending channel exists and somebody signs off on the first
 * letter. "Nothing is in the way" is a real answer and belongs on screen.
 *
 * THE REASON IT NAMES IS THE SENDER'S. `sendBlocker` walks the same preflight
 * list, in the same order, as the code that refuses a batch. Telling somebody
 * to switch the automation on while a hard stop is pulled would be true about
 * the automation and useless about the problem.
 */
import { AlertTriangle, CheckCircle2, Info, OctagonX } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import type { MarketingOverview } from "../../../../lib/marketingOverview";
import { Note } from "../shared";
import { sendBlocker } from "./lib";

export function GateNote({ view }: { view: MarketingOverview }) {
  const t = useT();
  const blocker = sendBlocker(view);

  if (!blocker) {
    return (
      <Note tone="info" icon={<CheckCircle2 className="size-3.5" />} className="mb-3.5">
        {t("marketing.overview.clear")}
      </Note>
    );
  }

  const stopped = blocker.kind === "stop" && blocker.code === "global_stop";
  return (
    <Note
      tone={stopped ? "bad" : blocker.kind === "stop" ? "warn" : "info"}
      icon={
        stopped ? <OctagonX className="size-3.5" />
        : blocker.kind === "stop" ? <AlertTriangle className="size-3.5" />
        : <Info className="size-3.5" />
      }
      className="mb-3.5"
    >
      <b>{t("marketing.overview.blocked.title")}</b>{" "}
      {blocker.kind === "stop"
        ? t(`marketing.run.stop.${blocker.code}`)
        : t(`marketing.overview.blocked.${blocker.code}`)}
      {/* The hard stop takes a written reason precisely so it can be read back
          months later. This is where it gets read back. */}
      {stopped && view.readiness.stopReason ? (
        <span className="mt-1 block">{t("marketing.overview.blocked.reason", { reason: view.readiness.stopReason })}</span>
      ) : null}
    </Note>
  );
}
